/**
 * Utility functions for masking personal details.
 * Used to display partial information before identity verification.
 */

/**
 * Masks a full name, showing first letter of each part.
 * e.g., "Thabo Mokoena" → "T**** M******"
 * @param {string} name - Full name
 * @returns {string} Masked name
 */
export function maskName(name) {
  if (!name) return '***';
  return name
    .split(' ')
    .map(part => {
      if (part.length <= 1) return part;
      return part[0] + '*'.repeat(part.length - 1);
    })
    .join(' ');
}

/**
 * Masks a phone number, showing only last 4 digits.
 * e.g., "0712345678" → "***-***-5678"
 * @param {string} phone - Phone number
 * @returns {string} Masked phone
 */
export function maskPhone(phone) {
  if (!phone) return '***-***-****';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length < 4) return '***-***-****';
  const lastFour = cleaned.slice(-4);
  return `***-***-${lastFour}`;
}

/**
 * Masks an email address, showing first letter and domain.
 * e.g., "t.mokoena@email.co.za" → "t*****@e****.co.za"
 * @param {string} email - Email address
 * @returns {string} Masked email
 */
export function maskEmail(email) {
  if (!email) return '***@***.***';
  const [local, domain] = email.split('@');
  if (!domain) return '***@***.***';
  const maskedLocal = local.length > 1
    ? local[0] + '*'.repeat(Math.min(local.length - 1, 5))
    : local;
  const domainParts = domain.split('.');
  const maskedDomain = domainParts.map((part, i) => {
    if (i === domainParts.length - 1) return part; // keep TLD
    if (i === domainParts.length - 2 && domainParts.length > 2) return part; // keep second-level TLD (.co.za)
    return part[0] + '*'.repeat(Math.min(part.length - 1, 4));
  }).join('.');
  return `${maskedLocal}@${maskedDomain}`;
}

/**
 * Masks an ID number, showing only last 4 digits.
 * e.g., "8805125088083" → "*********0883"
 * @param {string} idNumber - SA ID number
 * @returns {string} Masked ID
 */
export function maskIdNumber(idNumber) {
  if (!idNumber) return '*************';
  const cleaned = idNumber.replace(/\D/g, '').replace(/\*/g, '');
  if (cleaned.length < 4) return '*************';
  const lastFour = cleaned.slice(-4);
  return '*'.repeat(Math.max(cleaned.length - 4, 9)) + lastFour;
}

/**
 * Masks an account number, showing only last 4 characters.
 * e.g., "DM-PL-90147" → "***-**-*0147"
 * @param {string} accountNumber - Account number
 * @returns {string} Masked account number
 */
export function maskAccountNumber(accountNumber) {
  if (!accountNumber) return '****';
  if (accountNumber.length <= 4) return accountNumber;
  const lastFour = accountNumber.slice(-4);
  return '*'.repeat(accountNumber.length - 4) + lastFour;
}

/**
 * Format currency in South African Rand.
 * @param {number} amount - The amount
 * @returns {string} Formatted amount e.g., "R12,350.00"
 */
export function formatCurrency(amount) {
  if (amount == null || isNaN(amount)) return 'R0.00';
  return `R${Number(amount).toLocaleString('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
